import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  ShieldCheck, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, 
  Mail, Phone, Plus, User, Lock, Smartphone, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PreferenceCenterPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [titular, setTitular] = useState(null);
  const [consents, setConsents] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [company, setCompany] = useState({});
  
  // Revoke dialog
  const [revokeDialog, setRevokeDialog] = useState({ open: false, consent: null });
  const [revokeReason, setRevokeReason] = useState('');
  
  // Add consent dialog
  const [addConsentDialog, setAddConsentDialog] = useState(false);
  const [newConsentForm, setNewConsentForm] = useState({
    purposes: [],
    contact_type: 'email',
    contact_value: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/preferences/public/${token}`);
      setTitular(response.data.titular);
      setConsents(response.data.consents || []);
      setPurposes(response.data.purposes || []);
      setCompany(response.data.company || {});
      
      // Set default contact for new consent
      if (response.data.titular) {
        setNewConsentForm(prev => ({
          ...prev,
          contact_value: response.data.titular.email || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.detail || 'Link inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeConsent = async () => {
    if (!revokeDialog.consent) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/preferences/public/${token}/update`, {
        consent_id: revokeDialog.consent.consent_id,
        action: 'revoke',
        reason: revokeReason
      });
      
      toast.success('Consentimento revogado com sucesso');
      setRevokeDialog({ open: false, consent: null });
      setRevokeReason('');
      fetchData();
    } catch (err) {
      console.error('Error revoking:', err);
      toast.error(err.response?.data?.detail || 'Erro ao revogar consentimento');
    }
  };

  const handleRenewConsent = async (consent) => {
    try {
      await axios.post(`${BACKEND_URL}/api/preferences/public/${token}/update`, {
        consent_id: consent.consent_id,
        action: 'renew'
      });
      
      toast.success('Consentimento renovado por mais 1 ano');
      fetchData();
    } catch (err) {
      console.error('Error renewing:', err);
      toast.error(err.response?.data?.detail || 'Erro ao renovar consentimento');
    }
  };

  const handleAddConsent = async () => {
    if (newConsentForm.purposes.length === 0) {
      toast.error('Selecione pelo menos uma finalidade');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/preferences/public/${token}/add`, newConsentForm);
      
      toast.success('Consentimento adicionado com sucesso');
      setAddConsentDialog(false);
      setNewConsentForm({ purposes: [], contact_type: 'email', contact_value: titular?.email || '' });
      fetchData();
    } catch (err) {
      console.error('Error adding:', err);
      toast.error(err.response?.data?.detail || 'Erro ao adicionar consentimento');
    }
  };

  const handlePurposeToggle = (purposeId) => {
    setNewConsentForm(prev => ({
      ...prev,
      purposes: prev.purposes.includes(purposeId)
        ? prev.purposes.filter(p => p !== purposeId)
        : [...prev.purposes, purposeId]
    }));
  };

  const getPurposeName = (purposeId) => {
    const purpose = purposes.find(p => p.purpose_id === purposeId);
    return purpose?.name || purposeId;
  };

  const getContactOptions = () => {
    const options = [];
    if (titular?.email) {
      options.push({ type: 'email', value: titular.email, label: `Email: ${titular.email}` });
    }
    if (titular?.phone) {
      options.push({ type: 'phone', value: titular.phone, label: `Telefone: ${titular.phone}` });
    }
    if (titular?.mobile) {
      options.push({ type: 'mobile', value: titular.mobile, label: `Telemóvel: ${titular.mobile}` });
    }
    return options;
  };

  const getAvailablePurposes = () => {
    const consentedPurposes = new Set();
    consents.filter(c => c.status === 'active').forEach(c => {
      c.purposes?.forEach(p => consentedPurposes.add(p));
    });
    return purposes.filter(p => !consentedPurposes.has(p.purpose_id));
  };

  const activeConsents = consents.filter(c => c.status === 'active');
  const expiringConsents = activeConsents.filter(c => {
    if (!c.expiration_date) return false;
    const expDate = new Date(c.expiration_date);
    const thirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return expDate > new Date() && expDate <= thirtyDaysLater;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981] mx-auto mb-4"></div>
          <p className="text-slate-600">A carregar as suas preferências...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isExpired = error.includes('expirado') || error.includes('410');
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className={`w-16 h-16 ${isExpired ? 'bg-amber-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              {isExpired ? (
                <Clock className="w-8 h-8 text-amber-600" />
              ) : (
                <Lock className="w-8 h-8 text-red-600" />
              )}
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              {isExpired ? 'Link Expirado' : 'Acesso Negado'}
            </h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <p className="text-sm text-slate-500">
              {isExpired 
                ? 'Os links do Centro de Preferências são válidos por 24 horas. Contacte a organização para solicitar um novo link.'
                : 'Se recebeu este link por email e não consegue aceder, por favor contacte-nos.'
              }
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Toaster position="top-center" />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-8 h-8 text-[#10B981]" />
            <h1 className="text-2xl font-bold">Centro de Preferências</h1>
          </div>
          <p className="text-slate-300">
            Gerir os seus consentimentos de privacidade{company.name ? ` - ${company.name}` : ''}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-4xl py-8 space-y-6">
        {company.login_banner_url && (
          <img
            src={`${BACKEND_URL}${company.login_banner_url}`}
            alt={company.name || 'Banner'}
            data-testid="preference-center-banner"
            className="w-full rounded-lg border border-slate-200 shadow-sm object-cover"
            style={{ aspectRatio: '1820 / 400' }}
          />
        )}
        {/* User Info Card */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#10B981]/10 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-[#10B981]" />
              </div>
              <div>
                <CardTitle>{titular?.name || 'Titular'}</CardTitle>
                <CardDescription>{titular?.email}</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Expiring Alert */}
        {expiringConsents.length > 0 && (
          <Alert className="border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800">Consentimentos a Expirar</AlertTitle>
            <AlertDescription className="text-amber-700">
              Tem {expiringConsents.length} consentimento(s) a expirar nos próximos 30 dias. 
              Renove-os para continuar a receber comunicações.
            </AlertDescription>
          </Alert>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-[#10B981]">{activeConsents.length}</div>
              <p className="text-sm text-slate-600">Ativos</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-amber-500">{expiringConsents.length}</div>
              <p className="text-sm text-slate-600">A Expirar</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-slate-500">{consents.filter(c => c.status === 'revoked').length}</div>
              <p className="text-sm text-slate-600">Revogados</p>
            </CardContent>
          </Card>
        </div>

        {/* Consents Table */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#10B981]" />
                  Os Seus Consentimentos
                </CardTitle>
                <CardDescription>Gerir as suas preferências de comunicação</CardDescription>
              </div>
              {getAvailablePurposes().length > 0 && (
                <Button onClick={() => setAddConsentDialog(true)} className="bg-[#10B981] hover:bg-[#10B981]/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {consents.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Não tem consentimentos registados</p>
                <Button onClick={() => setAddConsentDialog(true)} className="mt-4 bg-[#10B981] hover:bg-[#10B981]/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Primeiro Consentimento
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Finalidade</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Expira</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {consents.map((consent) => {
                    const isExpired = consent.expiration_date && new Date(consent.expiration_date) < new Date();
                    const isExpiringSoon = consent.expiration_date && 
                      new Date(consent.expiration_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) &&
                      new Date(consent.expiration_date) > new Date();
                    
                    return (
                      <TableRow 
                        key={consent.consent_id} 
                        className={isExpired ? 'bg-red-50' : isExpiringSoon ? 'bg-amber-50' : ''}
                      >
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {consent.purposes?.map(p => (
                              <Badge key={p} variant="outline" className="text-xs">
                                {getPurposeName(p)}
                              </Badge>
                            ))}
                          </div>
                          {consent.consent_origin && (
                            <span className="text-xs text-slate-400 mt-1 block">
                              Origem: {consent.consent_origin}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {consent.contact_type === 'email' && <Mail className="w-4 h-4 text-slate-500" />}
                            {consent.contact_type === 'phone' && <Phone className="w-4 h-4 text-slate-500" />}
                            {consent.contact_type === 'mobile' && <Smartphone className="w-4 h-4 text-slate-500" />}
                            <span className="text-sm">{consent.contact_value}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {consent.status === 'active' && !isExpired ? (
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Ativo
                            </Badge>
                          ) : consent.status === 'pending' ? (
                            <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                              <Clock className="w-3 h-3 mr-1" />
                              Pendente
                            </Badge>
                          ) : isExpired ? (
                            <Badge className="bg-red-50 text-red-700 border-red-200">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Expirado
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                              <XCircle className="w-3 h-3 mr-1" />
                              Revogado
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {consent.expiration_date ? (
                            <span className={`text-sm ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>
                              {new Date(consent.expiration_date).toLocaleDateString('pt-PT')}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {consent.status === 'active' && (isExpiringSoon || isExpired) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRenewConsent(consent)}
                                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Renovar
                              </Button>
                            )}
                            {consent.status === 'active' && !isExpired && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setRevokeDialog({ open: true, consent })}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Revogar
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* GDPR Info */}
        <Card className="border-slate-200 bg-blue-50/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <ShieldCheck className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Os Seus Direitos RGPD</h3>
                <p className="text-sm text-slate-600 mb-2">
                  De acordo com o Regulamento Geral sobre a Proteção de Dados, tem direito a:
                </p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Aceder aos seus dados pessoais (Art. 15º)</li>
                  <li>• Retificar dados incorretos (Art. 16º)</li>
                  <li>• Solicitar a eliminação dos seus dados (Art. 17º)</li>
                  <li>• Revogar consentimentos a qualquer momento (Art. 7º)</li>
                </ul>
                {company.privacy_url && (
                  <a 
                    href={company.privacy_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                  >
                    Ver Política de Privacidade completa →
                  </a>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-slate-500 py-4">
          <p>Esta página é segura e está protegida.</p>
          <p className="mt-1">© {company.name || 'Privaxia'} - Em conformidade com o RGPD</p>
        </div>
      </div>

      {/* Revoke Dialog */}
      <Dialog open={revokeDialog.open} onOpenChange={(open) => setRevokeDialog({ open, consent: open ? revokeDialog.consent : null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Revogar Consentimento
            </DialogTitle>
            <DialogDescription>
              Ao revogar, deixará de receber comunicações relacionadas com estas finalidades.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <strong>Finalidades:</strong> {revokeDialog.consent?.purposes?.map(p => getPurposeName(p)).join(', ')}
              </p>
            </div>
            <div>
              <Label htmlFor="revoke-reason">Motivo (opcional)</Label>
              <Input
                id="revoke-reason"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                placeholder="Ex: Já não desejo receber comunicações"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeDialog({ open: false, consent: null })}>Cancelar</Button>
            <Button onClick={handleRevokeConsent} className="bg-red-600 hover:bg-red-700 text-white">
              Confirmar Revogação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Consent Dialog */}
      <Dialog open={addConsentDialog} onOpenChange={setAddConsentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#10B981]" />
              Adicionar Consentimento
            </DialogTitle>
            <DialogDescription>Selecione as finalidades para as quais deseja dar consentimento</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Canal de Contacto</Label>
              <Select 
                value={`${newConsentForm.contact_type}:${newConsentForm.contact_value}`}
                onValueChange={(value) => {
                  const [type, ...valueParts] = value.split(':');
                  setNewConsentForm(prev => ({
                    ...prev,
                    contact_type: type,
                    contact_value: valueParts.join(':')
                  }));
                }}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Selecione um contacto" />
                </SelectTrigger>
                <SelectContent>
                  {getContactOptions().map(option => (
                    <SelectItem key={`${option.type}:${option.value}`} value={`${option.type}:${option.value}`}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block">Finalidades</Label>
              <div className="space-y-2 border rounded-lg p-3 bg-slate-50 max-h-64 overflow-y-auto">
                {getAvailablePurposes().length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Já tem consentimento ativo para todas as finalidades.
                  </p>
                ) : (
                  getAvailablePurposes().map(purpose => (
                    <div key={purpose.purpose_id} className="flex items-start space-x-3 p-2 hover:bg-white rounded">
                      <Checkbox
                        id={`purpose-${purpose.purpose_id}`}
                        checked={newConsentForm.purposes.includes(purpose.purpose_id)}
                        onCheckedChange={() => handlePurposeToggle(purpose.purpose_id)}
                      />
                      <div className="grid gap-0.5 leading-none">
                        <label htmlFor={`purpose-${purpose.purpose_id}`} className="text-sm font-medium cursor-pointer">
                          {purpose.name}
                        </label>
                        <p className="text-xs text-slate-500">{purpose.description}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddConsentDialog(false)}>Cancelar</Button>
            <Button 
              onClick={handleAddConsent} 
              className="bg-[#10B981] hover:bg-[#10B981]/90"
              disabled={newConsentForm.purposes.length === 0}
            >
              Adicionar Consentimento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreferenceCenterPage;
